import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import { Card, Col, Form, FormGroup, Modal, ModalBody, Row, Spinner, Button } from "reactstrap";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  CustomerInvestmentTable,
  PaginationComponent,
  RSelect,
  Icon,
} from "../../../components/Component";
import { authHeader } from "../../../header";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { message } from "antd";
import { toast } from "react-toastify";
import moment from "moment";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAllBanks, verifyBankAccount } from "./customerInvestmentApi";

// import { useDebounce } from "../../../hooks/useDebounceSearch";

export const CustomerInvestment = () => {
  const [isLoadBtn, setIsLoadBtn] = useState(false);
  const [modal, setModal] = useState({
    add: false,
    topup: false,
    withdraw: false,
    transfer: false,
  });
  const [allBanks, setAllBanks] = useState([]);
  const [details, setDetails] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [proceed, setProceed] = useState(false);
  const [withdrawalType, setWithdrawalType] = useState("PARTIAL");
  const [accountName, setAccountName] = useState("");
  const [formData, setFormData] = useState({
    amount: "",
    completed: true,
    bankAccountDetails: "",
    corporateUserWithdrawalMandate: "",
    customerInstructionImage: "",
    encodedUpload: "",
    name: "",
    email: "",
    paymentProofImage: "",
    paymentType: null,
    penalCharge: 0,
    plan: "",
    planToReceive: 0,
    planAction: "",
    withdrawTo: "",
    withdrawType: "",
    withdrawalReason: "",
    withdrawalReasonText: "",
  });

  useEffect(() => {
    console.log(formData);
  }, [formData]);

  const withdrawalTypeEnum = {
    FULL_WITHDRAWAL: "FULL",
    PARTIAL_WITHDRAWAL: "PARTIAL",
  };

  const validateAmount = (str) => /^[0-9]*\.?[0-9]*$/.test(str) || str === "";
  const validateBankAccount = (str) => /^[0-9]{10}$/.test(str);

  const admin_user = localStorage.getItem("userInfo");
  const userInfo = admin_user ? JSON.parse(admin_user) : {};
  const { register, handleSubmit, errors } = useForm();
  const queryClient = useQueryClient();

  // Update formData.email when details or userInfo changes
  useEffect(() => {
    if (details && Object.keys(details).length > 0) {
      const emailToUse = details?.userEmail || userInfo.email || "";
      if (!details.userEmail && userInfo.email) {
        console.log("useEffect - Falling back to userInfo.email:", userInfo.email);
      }
      setFormData((prev) => ({ ...prev, email: emailToUse }));
    }
  }, [details, userInfo.email]);

  // Fetch all investments
  const {
    data: investmentsData,
    isLoading,
    // error,
  } = useQuery(
    ["investments", search, currentPage],
    async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/auth/admin/trcreate-plan?search=${search}&creationSource=BACKEND&page=${
          currentPage - 1
        }&limit=${itemPerPage}`,
        { headers: authHeader() },
      );
      return response.data.data.body;
    },
    {
      keepPreviousData: true,
      onError: () => message.error("Failed to fetch investments"),
    },
  );

  // Fetch withdrawal reasons
  const { data: withdrawalReasons } = useQuery(
    ["withdrawalReasons"],
    async () => {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/trwithdrawal`, {
        headers: authHeader(),
      });
      return response.data.data.body;
    },
    {
      onError: () => message.error("Failed to fetch withdrawal reasons"),
    },
  );

  // Fetch penal charges
  const { data: penalChargeList } = useQuery(
    ["penalCharges"],
    async () => {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/trpenal-charge`, {
        headers: authHeader(),
      });
      return response.data.data.body;
    },
    {
      onError: () => message.error("Failed to fetch penal charges"),
    },
  );

  // Fetch user investments
  const { data: userInvestments } = useQuery(
    ["userInvestments", formData.email],
    async () => {
      if (!formData.email) {
        console.error("getUserInvestments Error: Email is undefined or empty");
        return [];
      }
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/user/trcreate-plan/${formData.email}`, {
        headers: authHeader(),
      });
      return response.data.data.body;
    },
    {
      enabled: !!formData.email,
      onError: (error) => console.error("getUserInvestments Error:", error.message),
    },
  );

  // Top-up mutation
  const topUpMutation = useMutation(
    async (payload) => {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/customer-investment/initiate-topup`,
        payload,
        { headers: authHeader() },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["investments"]);
        setModal((prev) => ({ ...prev, topup: false }));
        resetForm();
        message.success("Plan successfully topped up");
      },
      onError: (error) => {
        message.error(error.response?.data?.message || "Topup failed");
        setIsLoadBtn(false);
      },
      onSettled: () => setIsLoadBtn(false),
    },
  );

  // Transfer mutation
  const transferMutation = useMutation(
    async (payload) => {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/customer-investment/transfer`,
        payload,
        { headers: authHeader() },
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data?.object?.actionLogId) {
          completeTransferMutation.mutate(data.object.actionLogId);
        } else {
          queryClient.invalidateQueries(["investments"]);
          setModal((prev) => ({ ...prev, transfer: false }));
          resetForm();
          message.success("Plan successfully transferred");
        }
      },
      onError: (error) => {
        message.error(error.response?.data?.message || "Transfer failed");
        setIsLoadBtn(false);
      },
      onSettled: () => setIsLoadBtn(false),
    },
  );

  // Complete transfer mutation
  const completeTransferMutation = useMutation(
    async (id) => {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/trplan-action/complete-transfer?id=${id}`,
        {},
        { headers: authHeader() },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["investments"]);
        setModal((prev) => ({ ...prev, transfer: false }));
        resetForm();
        message.success("Plan successfully transferred");
      },
      onError: (error) => {
        message.error(error.response?.data?.message || "Transfer completion failed");
        setIsLoadBtn(false);
      },
      onSettled: () => setIsLoadBtn(false),
    },
  );

  // Withdraw mutation
  const withdrawMutation = useMutation(
    async (payload) => {
      if (!payload.email) {
        throw new Error("Email is required for withdrawal");
      }
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/customer-investment/withdraw`,
        payload,
        { headers: authHeader() },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["investments"]);
        setModal((prev) => ({ ...prev, withdraw: false }));
        resetForm();
        message.success("Withdrawal successful");
      },
      onError: (error) => {
        message.error(error.response?.data?.message || error.message);
        setIsLoadBtn(false);
      },
      onSettled: () => setIsLoadBtn(false),
    },
  );

  const resetForm = () => {
    setFormData({
      amount: "",
      completed: true,
      bankAccountDetails: "",
      corporateUserWithdrawalMandate: "",
      customerInstructionImage: "",
      encodedUpload: "",
      name: "",
      email: details?.userEmail || userInfo.email || "",
      paymentProofImage: "",
      paymentType: null,
      penalCharge: 0,
      plan: "",
      planToReceive: 0,
      planAction: "",
      withdrawTo: "",
      withdrawType: "",
      withdrawalReason: "",
      withdrawalReasonText: "",
    });
    setProceed(false);
    setWithdrawalType(withdrawalTypeEnum.PARTIAL_WITHDRAWAL);
  };

  const onFormCancel = () => {
    resetForm();
    setModal({ add: false, topup: false, transfer: false, withdraw: false });
    setDetails({});
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const onTopUpClick = (item) => {
    console.log("TopUp Click - Details:", item);
    const email = item?.userEmail || userInfo.email || "";
    if (!item?.userEmail) {
      console.error("onTopUpClick Error: userEmail is undefined, falling back to:", email);
    }
    setFormData((prev) => ({ ...prev, email }));
    setDetails(item);
    setModal((prev) => ({ ...prev, topup: true }));
  };

  const onTransferClick = (item) => {
    console.log("Transfer Click - Details:", item);
    const email = item?.userEmail || userInfo.email || "";
    if (!item?.userEmail) {
      console.error("onTransferClick Error: userEmail is undefined, falling back to:", email);
    }
    setFormData((prev) => ({ ...prev, email }));
    setDetails(item);
    setModal((prev) => ({ ...prev, transfer: true }));
  };

  const onWithdrawalClick = (item) => {
    console.log("Withdrawal Click - Details:", item);
    const email = item?.userEmail || userInfo.email || "";
    if (!item?.userEmail) {
      console.error("onWithdrawalClick Error: userEmail is undefined, falling back to:", email);
    }
    setFormData((prev) => ({ ...prev, email }));
    setDetails(item);
    setModal((prev) => ({ ...prev, withdraw: true }));
  };

  const handleWithdrawalTypeChange = (type) => {
    setWithdrawalType(type);
    if (type === withdrawalTypeEnum.FULL_WITHDRAWAL) {
      setFormData((prev) => ({ ...prev, amount: details?.planSummary?.principal }));
    } else {
      setFormData((prev) => ({ ...prev, amount: "" }));
    }
  };

  const getBankCode = (banks, bankName) => {
    const bank = banks.find((b) => b.name.toLowerCase() === bankName.toLowerCase());
    return bank ? bank.code : null;
  };

  let checkBankAccountTimeout;

  async function checkBankAccount(accountNumber) {
    // Clear previous debounce timer
    if (checkBankAccountTimeout) {
      clearTimeout(checkBankAccountTimeout);
    }

    // Only continue if 9–11 digits
    if (!/^\d{9,11}$/.test(accountNumber)) {
      return;
    }

    // Debounce for 2 seconds
    checkBankAccountTimeout = setTimeout(async () => {
      try {
        const userInfo = localStorage.getItem("userInfo");

        const response = await verifyBankAccount({
          bankCode: getBankCode(allBanks, formData?.currentBank),
          accountNumber: accountNumber,
          // bankCode: `001`, // test bank
          verifyBusinessBankAccount: true,
          userId: `${details?.id}`,
        });

        console.log(response);

        if (response?.success) {
          setAccountName(response?.data);
        } else {
          setAccountName("FAILED");
          toast.error(response?.message || "Something went wrong");
        }
      } catch (error) {
        setAccountName("FAILED");
        toast.error(error?.message || "Something went wrong");
      }
    }, 2000);
  }

  const onInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "amount" && !validateAmount(value)) {
      return;
    }
    if (name === "bankAccountDetails" && validateBankAccount(value)) {
      setFormData({ ...formData, [name]: value });
      checkBankAccount(value);
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (e, name) => {
    setFormData({ ...formData, [name]: e.value });
  };

  const handleInvestmentFileChange = (e, name) => {
    try {
      const { files } = e.target;
      if (!files || files.length === 0) return;

      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/svg+xml",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
        "text/csv",
      ];

      const allowedExtensions = [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".webp",
        ".svg",
        ".pdf",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".txt",
        ".csv",
      ];

      const maxSize = 2000000;
      const file = files[0];

      if (!file) return;

      if (file.size > maxSize) {
        alert(`File size must be less than ${maxSize / 1000000}MB`);
        e.target.value = "";
        return;
      }

      const fileName = typeof file.name === "string" ? file.name.toLowerCase() : "";
      const hasValidType = allowedTypes.includes(file.type);
      const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));

      if (!hasValidType && !hasValidExtension) {
        alert("File type not supported. Allowed types: images, PDF, Word, Excel, text files.");
        e.target.value = "";
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        try {
          const result = reader.result;

          if (typeof result !== "string") {
            alert("Failed to read file. Please try again.");
            e.target.value = "";
            return;
          }

          const parts = result.split("base64,");
          const encoded = parts.length === 2 && parts[1] ? parts[1] : null;

          if (!encoded) {
            alert("Failed to encode file. Please try again.");
            e.target.value = "";
            return;
          }

          setFormData((prevData) => ({
            ...prevData,
            [name]: {
              name: file.name,
              encodedUpload: encoded,
              size: file.size,
              type: file.type,
            },
          }));
        } catch (innerError) {
          console.error("Error processing file result:", innerError);
          alert("Error processing file. Please try again.");
          e.target.value = "";
        }
      };

      reader.onerror = () => {
        console.error("FileReader error:", reader.error);
        alert("Error reading file. Please try again.");
        e.target.value = "";
      };

      reader.readAsDataURL(file);
    } catch (outerError) {
      console.error("handleInvestmentFileChange error:", outerError);
      alert("Unexpected error. Please try again.");
    }
  };

  const computePenalCharge = useCallback(
    (intRecOption) => {
      const planProductCharges = penalChargeList
        ? penalChargeList.filter((data) => data?.product?.id === details?.product?.id)
        : [];
      let penalRate = 0;
      let penalCharge = 0;
      const maxNumberDays = moment(details?.actualMaturityDate).diff(details?.planSummary?.startDate, "days");
      const recentDate = moment().format("YYYY-MM-DD");
      const numOfDays = moment(recentDate).diff(details?.planSummary?.startDate, "days");
      const currentNumberOfDays = numOfDays === 0 ? 1 : numOfDays;
      const penalDays = moment(details?.planSummary?.endDate).diff(recentDate, "days");

      const penalObj = planProductCharges
        ?.sort((a, b) => a.minDaysElapsed - b.minDaysElapsed)
        ?.find((item) => {
          const maxDays = Math.floor((item.maxDaysElapsed * maxNumberDays) / 100);
          const minDays = Math.floor((item.minDaysElapsed * maxNumberDays) / 100);
          return currentNumberOfDays >= minDays && currentNumberOfDays <= maxDays;
        });

      penalRate = penalObj?.penalRate / 100;

      if (penalDays > 0) {
        switch (intRecOption) {
          case "MATURITY":
          case "BACKEND_MATURITY":
            if (details?.product?.properties?.penaltyFormula === "FIXED_FORMULA") {
              penalCharge = (currentNumberOfDays * penalRate * formData?.amount) / 365;
            } else if (details?.product?.properties?.penaltyFormula === "TARGET_FORMULA") {
              const totalEarnedInt =
                (details?.planSummary?.principal * details?.interestRate * (currentNumberOfDays / 365)) / 100;
              penalCharge = totalEarnedInt * penalRate;
            }
            break;

          case "UPFRONT":
          case "BACKEND_UPFRONT":
            if (details?.product?.properties?.penaltyFormula === "FIXED_FORMULA") {
              const excessIntPaid =
                formData.amount * (details?.interestRate / 100) * (maxNumberDays / 365) -
                formData.amount * (details?.interestRate / 100) * (currentNumberOfDays / 365);
              penalCharge = (currentNumberOfDays / 365) * penalRate * formData.amount + excessIntPaid;
            }
            break;

          case "MONTHLY":
          case "QUARTERLY":
          case "BI_ANNUAL":
          case "BACKEND_MONTHLY":
          case "BACKEND_QUARTERLY":
          case "BACKEND_BI_ANNUAL":
            if (details?.product?.properties?.penaltyFormula === "FIXED_FORMULA") {
              penalCharge = (currentNumberOfDays / 365) * penalRate * formData.amount;
            }
            break;

          default:
            penalCharge = 0;
            break;
        }
      }
      return penalCharge.toFixed(2);
    },
    [details, formData.amount, penalChargeList],
  );

  useEffect(() => {
    const penal = computePenalCharge(details?.interestReceiptOption);
    setFormData((prev) => ({ ...prev, penalCharge: penal }));
  }, [computePenalCharge, details]);

  const handleTopupSubmit = async () => {
    if (!formData.email) {
      console.error("handleTopupSubmit Error: formData.email is empty");
      message.error("Email is required for topup");
      return;
    }
    setIsLoadBtn(true);
    const planForm = {
      amount: parseFloat(formData.amount),
      paymentType: "DEBIT_CARD",
      planId: details?.id,
      planToReceiveId: details?.id,
      paymentProofImage: formData.paymentProofImage,
      email: formData.email,
      roleId: 1,
    };
    topUpMutation.mutate(planForm);
  };

  const handleTransferSubmit = async () => {
    if (!formData.email) {
      console.error("handleTransferSubmit Error: formData.email is empty");
      message.error("Email is required for transfer");
      return;
    }
    setIsLoadBtn(true);
    const planForm = {
      amount: parseInt(formData.amount),
      customerInstructionImage: formData.customerInstructionImage,
      completed: true,
      planId: details?.id,
      paymentType: "DEBIT_CARD",
      email: formData.email,
      planToReceiveId: formData.planToReceive,
      roleId: 1,
    };
    transferMutation.mutate(planForm);
  };

  const handleWithdrawSubmit = async () => {
    if (!formData.email) {
      console.error("handleWithdrawSubmit Error: formData.email is empty");
      message.error("Email is required for withdrawal");
      return;
    }
    setIsLoadBtn(true);
    const isToOthers = formData.withdrawTo === "WITHDRAW_TO_OTHERS";
    const isFullWithdrawal = withdrawalType === withdrawalTypeEnum.FULL_WITHDRAWAL;
    const planForm = {
      amount: parseInt(formData.amount),
      bankAccountDetails: formData.bankAccountDetails, // ✅ fixed key name
      customerInstructionImage: formData.customerInstructionImage,
      email: formData.email,
      penalCharge: parseFloat(formData.penalCharge) || 0,
      planId: details?.id,
      planAction: "WITHDRAW", // ✅ fixed for TO_BANK
      withdrawTo: isToOthers ? "TO_BANK" : formData.withdrawTo, // ✅ kept others logic
      withdrawType: withdrawalType,
      withdrawalReason:
        formData.withdrawalReason === "Other" ? formData.withdrawalReasonText : formData.withdrawalReason,
      roleId: 1,
      paymentType: "BANK_TRANSFER",
      bankName: formData.currentBank,
      transactionType: isToOthers ? "WITHDRAWAL_TO_OTHERS" : null, // ✅ kept for others
    };
    withdrawMutation.mutate(planForm);
  };

  const activeWithdrawReasons = withdrawalReasons?.filter((item) => item.status === "ACTIVE");
  const withdrawalReasonsMapped = activeWithdrawReasons?.map((val) => ({
    label: val.reason,
    value: val.id,
  }));
  const withdrawalReasonsOptions = [...(withdrawalReasonsMapped || []), { label: "Other", value: "Other" }];

  const transferPlanOptions =
    userInvestments
      ?.filter(
        ({ planStatus, interestReceiptOption, useraccount, product, id }) =>
          planStatus === "ACTIVE" &&
          (interestReceiptOption === "MATURITY" || interestReceiptOption === "BACKEND_MATURITY") &&
          useraccount?.id === details?.useraccount?.id &&
          product?.properties?.allowsTopUp &&
          id !== details?.id,
      )
      ?.map((ten) => ({
        label: ten.planName,
        value: ten.id,
      })) ?? [];

  const currentItems = investmentsData?.entities || [];
  const totalItems = investmentsData?.total || 0;

  async function fetchAndSetBanks() {
    try {
      const response = await fetchAllBanks();
      console.log("RAW fetchAllBanks response:", response); // 👈 check this
      if (response?.success) {
        setAllBanks(response?.data);
      } else {
        toast.error(response?.message || "Failed to fetch banks");
      }
    } catch (error) {
      console.log("RAW error:", error); // 👈 and this
      toast.error(error?.message || "Something went wrong");
    }
  }

  useEffect(() => {
    fetchAndSetBanks();
  }, []); // ✅ Only runs once on mount

  // setInterval(()=>{
  //   console.log(formData);
  // },1200)

  return (
    <React.Fragment>
      <Head title="Customer Investments" />
      <Content>
        <Block size="lg">
          <BlockHead>
            <BlockHeadContent>
              <div className="d-flex justify-content-between align-items-center">
                <BlockTitle tag="h4">Customer Investments</BlockTitle>
                <div className="d-flex align-items-center justify-center">
                  <div>
                    <input
                      className="form-control"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Search Investment..."
                    />
                  </div>
                </div>
              </div>
            </BlockHeadContent>
          </BlockHead>
          <Card className="card-bordered card-preview" style={{ width: "100%", overflowX: "auto" }}>
            {isLoading ? (
              <Spinner
                color="primary"
                style={{
                  height: "3rem",
                  width: "3rem",
                  margin: "0 auto",
                }}
              />
            ) : (
              <CustomerInvestmentTable
                onTransferClick={onTransferClick}
                customerInvestments={currentItems}
                onTopUpClick={onTopUpClick}
                onWithdrawalClick={onWithdrawalClick}
              />
            )}
          </Card>
          <div className="card-inner">
            {totalItems > 0 ? (
              <PaginationComponent
                itemPerPage={itemPerPage}
                totalItems={totalItems}
                paginate={(pageNumber) => setCurrentPage(pageNumber)}
                currentPage={currentPage}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">No Request found</span>
              </div>
            )}
          </div>
        </Block>

        <Modal
          isOpen={modal.topup}
          toggle={() => {
            setModal({ topup: false });
            onFormCancel();
          }}
          className="modal-dialog-centered"
          size="lg"
        >
          <ModalBody>
            <a href="#cancel" onClick={onFormCancel} className="close">
              <Icon name="cross-sm" />
            </a>
            <div className="p-2">
              <h5 className="title">Top Up Investment</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(handleTopupSubmit)}>
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Active Plan</label>
                      <div className="rounded bg-gray text-white p-2 font-weight-bold">
                        <p>{details?.planName ?? "N/A"}</p>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Amount to Top Up</label>
                      <input
                        type="text"
                        name="amount"
                        value={formData.amount}
                        placeholder="0.00"
                        onChange={onInputChange}
                        className="form-control"
                        ref={register({ required: "This field is required" })}
                      />
                      {errors.amount && <span className="invalid">{errors.amount.message}</span>}
                      {parseFloat(formData.amount) < 0 && (
                        <span className="invalid">Top up amount cannot be less than 0</span>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Upload Payment Proof</label>
                      <input
                        type="file"
                        name="paymentProofImage"
                        onChange={(e) => handleInvestmentFileChange(e, "paymentProofImage")}
                        className="form-control"
                        ref={register({ required: "This field is required" })}
                      />
                      {errors.paymentProofImage && <span className="invalid">{errors.paymentProofImage.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        {isLoadBtn ? (
                          <Button color="primary" disabled>
                            <Spinner size="sm">Loading...</Spinner>
                            <span> Loading</span>
                          </Button>
                        ) : (
                          <Button color="primary" size="md" type="submit" disabled={parseFloat(formData.amount) < 0}>
                            Submit
                          </Button>
                        )}
                      </li>
                      <li>
                        <Button onClick={onFormCancel} className="link link-light">
                          Cancel
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={modal.transfer}
          toggle={() => {
            setModal((prev) => ({ ...prev, transfer: false }));
            onFormCancel();
          }}
          className="modal-dialog-centered"
          size="lg"
        >
          <ModalBody>
            <a href="#cancel" onClick={onFormCancel} className="close">
              <Icon name="cross-sm" />
            </a>
            <div className="p-2">
              <h5 className="title">Transfer Investment</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(handleTransferSubmit)}>
                  <Col md="12">
                    <FormGroup>
                      <label className="form-label">Active Plan</label>
                      <div className="rounded bg-gray text-white p-2 font-weight-bold">
                        <p>{details?.planName ?? "N/A"}</p>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Select Plan</label>
                      <RSelect
                        placeholder="Select Plan"
                        name="planToReceive"
                        options={transferPlanOptions}
                        onChange={(e) => handleSelectChange(e, "planToReceive")}
                        value={transferPlanOptions.filter(({ value }) => value === formData.planToReceive)}
                        // ref={register({ required: "This field is required" })}
                      />
                      {errors.planToReceive && <span className="invalid">{errors.planToReceive.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Amounts to send</label>
                      <input
                        type="text"
                        name="amount"
                        placeholder="Enter Amount"
                        value={formData.amount}
                        onChange={onInputChange}
                        className="form-control"
                        ref={register({ required: "This field is required" })}
                      />
                      {errors.amount && <span className="invalid">{errors.amount.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Balance</label>
                      <input
                        type="text"
                        name="balance"
                        disabled
                        placeholder="Available Balance"
                        value={
                          formData.amount !== ""
                            ? parseFloat(formData.amount) > 0
                              ? details?.planSummary?.principal - parseFloat(formData.amount)
                              : 0
                            : 0
                        }
                        className="form-control"
                        ref={register({ required: "This field is required" })}
                      />
                      {errors.balance && <span className="invalid">{errors.balance.message}</span>}
                      {details?.planSummary?.principal - parseInt(formData.amount) <
                        details?.product?.minTransactionLimit && (
                        <span className="invalid">
                          Balance after transfer can not be less than the product minimum value{" "}
                          {details?.product?.minTransactionLimit}
                        </span>
                      )}
                      {parseFloat(formData.amount) < 0 && (
                        <span className="invalid">Transfer amount cannot be less than 0</span>
                      )}
                    </FormGroup>
                  </Col>
                  {!proceed && (
                    <Col md="12">
                      <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                        <li>
                          <Button
                            color="primary"
                            size="md"
                            disabled={parseFloat(formData.amount) < 0}
                            onClick={(e) => {
                              e.preventDefault();
                              setProceed(true);
                            }}
                          >
                            Proceed
                          </Button>
                        </li>
                      </ul>
                    </Col>
                  )}
                  {proceed && (
                    <Col size="12">
                      <Col md="12">
                        <FormGroup>
                          <label className="form-label">Upload Customer's Instructions</label>
                          <input
                            type="file"
                            name="customerInstructionImage"
                            placeholder="Select Customer's Instructions"
                            onChange={(e) => handleInvestmentFileChange(e, "customerInstructionImage")}
                            className="form-control"
                            ref={register({ required: "This field is required" })}
                          />
                          {errors.customerInstructionImage && (
                            <span className="invalid">{errors.customerInstructionImage.message}</span>
                          )}
                        </FormGroup>
                      </Col>
                      <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                        <li>
                          {isLoadBtn ? (
                            <Button color="primary" disabled>
                              <Spinner size="sm">Loading...</Spinner>
                              <span> Loading</span>
                            </Button>
                          ) : (
                            <Button
                              color="primary"
                              size="md"
                              type="submit"
                              disabled={
                                details?.planSummary?.principal - parseInt(formData.amount) <
                                details?.product?.minTransactionLimit
                              }
                            >
                              Submit
                            </Button>
                          )}
                        </li>
                        <li>
                          <Button onClick={onFormCancel} className="link link-light">
                            Cancel
                          </Button>
                        </li>
                      </ul>
                    </Col>
                  )}
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={modal.withdraw}
          toggle={() => {
            setModal((prev) => ({ ...prev, withdraw: false }));
            onFormCancel();
          }}
          className="modal-dialog-centered"
          size="lg"
        >
          <ModalBody>
            <a href="#cancel" onClick={onFormCancel} className="close">
              <Icon name="cross-sm" />
            </a>
            <div className="p-2">
              <h5 className="title d-flex align-items-center">
                {proceed && (
                  <Icon
                    name="chevron-left"
                    className="mr-2"
                    style={{ cursor: "pointer" }}
                    onClick={() => setProceed(false)}
                  />
                )}
                {proceed ? "Upload Request Mandate" : "Withdraw Investment"}
              </h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(handleWithdrawSubmit)}>
                  {!proceed ? (
                    <>
                      <Col md="12">
                        <FormGroup>
                          <label className="form-label" style={{ textTransform: "capitalize" }}>
                            {details?.planStatus?.toLowerCase()} Plan
                          </label>
                          <div className="rounded bg-gray text-white p-2 font-weight-bold">
                            <p>{details?.planName ?? "N/A"}</p>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="12">
                        <Row md="12" className="d-flex justify-content-between px-3">
                          <Col
                            md="5"
                            className={`p-2 rounded text-center ${
                              withdrawalType === withdrawalTypeEnum.PARTIAL_WITHDRAWAL
                                ? "bg-primary text-white font-weight-bold"
                                : "bg-gray"
                            }`}
                            style={{
                              cursor: "pointer",
                              opacity: withdrawalType === withdrawalTypeEnum.PARTIAL_WITHDRAWAL ? "1" : "0.5",
                            }}
                            onClick={() => handleWithdrawalTypeChange(withdrawalTypeEnum.PARTIAL_WITHDRAWAL)}
                          >
                            Partial Withdrawal
                          </Col>
                          <Col
                            md="5"
                            className={`${
                              withdrawalType === withdrawalTypeEnum.FULL_WITHDRAWAL
                                ? "bg-primary text-white font-weight-bold"
                                : "bg-gray"
                            } p-2 rounded text-center`}
                            style={{
                              cursor: "pointer",
                              opacity: withdrawalType === withdrawalTypeEnum.FULL_WITHDRAWAL ? "1" : "0.5",
                            }}
                            onClick={() => handleWithdrawalTypeChange(withdrawalTypeEnum.FULL_WITHDRAWAL)}
                          >
                            Full Withdrawal
                          </Col>
                        </Row>
                      </Col>
                      <Col md="12">
                        <FormGroup>
                          <label className="form-label">Withdrawal Reason</label>
                          <RSelect
                            placeholder="Please select a withdrawal Reason."
                            options={withdrawalReasonsOptions}
                            onChange={(e) => handleSelectChange(e, "withdrawalReason")}
                            value={withdrawalReasonsOptions.find(({ value }) => value === formData.withdrawalReason)}
                            // ref={register({ required: "This field is required" })}
                          />
                          {errors.withdrawalReason && (
                            <span className="invalid">{errors.withdrawalReason.message}</span>
                          )}
                        </FormGroup>
                      </Col>
                      {withdrawalReasonsOptions.find(({ value }) => value === formData.withdrawalReason)?.value ===
                        "Other" && (
                        <Col md="12">
                          <FormGroup>
                            <label className="form-label">Please Provide a reason for withdrawal.</label>
                            <textarea
                              placeholder="Please provide a reason for withdrawal."
                              name="withdrawalReasonText"
                              value={formData.withdrawalReasonText}
                              onChange={onInputChange}
                              className="form-control"
                              ref={register({ required: "This field is required" })}
                            />
                            {errors.withdrawalReasonText && (
                              <span className="invalid">{errors.withdrawalReasonText.message}</span>
                            )}
                          </FormGroup>
                        </Col>
                      )}
                      <Col md="12">
                        <FormGroup>
                          <label className="form-label">Withdrawal Summary.</label>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Amount To Liquidate</label>
                          <input
                            type="text"
                            name="amount"
                            value={formData.amount}
                            placeholder="Enter Balance"
                            disabled={withdrawalType === withdrawalTypeEnum.FULL_WITHDRAWAL}
                            onChange={onInputChange}
                            className="form-control"
                            ref={register({ required: "This field is required" })}
                          />
                          {errors.amount && <span className="invalid">{errors.amount.message}</span>}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Balance</label>
                          <p className="text-lg font-weight-bold">
                            {formData.amount !== ""
                              ? details?.planSummary?.principal - parseFloat(formData.amount)
                              : details?.planSummary?.principal}
                          </p>
                          {errors.balance && <span className="invalid">{errors.balance.message}</span>}
                          {details?.planSummary?.principal - parseInt(formData.amount) <
                            details?.product?.minTransactionLimit &&
                            withdrawalType === withdrawalTypeEnum.PARTIAL_WITHDRAWAL && (
                              <span className="invalid" style={{ color: "red" }}>
                                Balance after withdraw can not be less than the product minimum value{" "}
                                {details?.product?.minTransactionLimit}
                              </span>
                            )}
                          {parseFloat(formData.amount) < 0 && (
                            <span className="invalid" style={{ color: "red" }}>
                              Withdraw amount can not be less than 0
                            </span>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Plan Name</label>
                          <div className="rounded bg-gray text-white p-2 font-weight-bold">
                            <p>{details?.planName ?? "N/A"}</p>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Start Date</label>
                          <div className="rounded bg-gray text-white p-2 font-weight-bold">
                            <p>{details?.planSummary?.startDate ?? "N/A"}</p>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">End Date</label>
                          <div className="rounded bg-gray text-white p-2 font-weight-bold">
                            <p>{details?.planSummary?.endDate ?? "N/A"}</p>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Plan Balance Before Liquidation</label>
                          <div className="rounded bg-gray text-white p-2 font-weight-bold">
                            <p>{details?.planSummary?.principal ?? "N/A"}</p>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Withdrawal Amount</label>
                          <div className="rounded bg-gray text-white p-2 font-weight-bold">
                            <p>{formData.amount !== "" ? formData.amount : 0}</p>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Penal Charges</label>
                          <div className="rounded bg-gray text-white p-2 font-weight-bold">
                            <p>{formData.penalCharge ?? "N/A"}</p>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Available Plan Balance</label>
                          <div className="rounded bg-gray text-white p-2 font-weight-bold">
                            <p>
                              {formData.amount !== ""
                                ? parseFloat(formData.amount) > 0
                                  ? withdrawalType === withdrawalTypeEnum.FULL_WITHDRAWAL
                                    ? (details?.planSummary?.principal - parseFloat(formData.amount))?.toFixed(2)
                                    : (
                                        details?.planSummary?.principal -
                                        (parseFloat(formData.amount) + parseFloat(formData.penalCharge))
                                      )?.toFixed(2)
                                  : details?.planSummary?.principal
                                : details?.planSummary?.principal?.toFixed(2)}
                            </p>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Reason For Withdrawal</label>
                          <div className="rounded bg-gray text-white p-2 font-weight-bold">
                            <p>
                              {withdrawalReasons?.find(({ id }) => id === formData.withdrawalReason)
                                ? withdrawalReasons.find(({ id }) => id === formData.withdrawalReason)?.reason
                                : withdrawalReasonsOptions.find(({ value }) => value === formData.withdrawalReason)
                                      ?.value === "Other"
                                  ? formData.withdrawalReasonText
                                  : "Choose a reason"}
                            </p>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md="12">
                        <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                          <li>
                            <Button
                              color="primary"
                              size="md"
                              onClick={(e) => {
                                e.preventDefault();
                                setProceed(true);
                              }}
                              disabled={parseFloat(formData.amount) < 0}
                            >
                              Proceed
                            </Button>
                          </li>
                          <li>
                            <Button onClick={onFormCancel} className="link link-light">
                              Cancel
                            </Button>
                          </li>
                        </ul>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Select Withdrawal Destination</label>
                          <RSelect
                            placeholder="Select Withdrawal Destination"
                            options={[
                              { label: "Withdraw To Bank", value: "TO_BANK" },
                              ...(userInfo?.role?.name === "SUPER_ADMINISTRATOR"
                                ? [{ label: "Withdraw To Others", value: "WITHDRAW_TO_OTHERS" }] // ✅
                                : []),
                            ]}
                            onChange={(e) => handleSelectChange(e, "withdrawTo")}
                            // ref={register({ required: "This field is required" })}
                          />
                          {errors.withdrawTo && <span className="invalid">{errors.withdrawTo.message}</span>}
                        </FormGroup>
                      </Col>
                      {/* {(formData?.withdrawTo === "WITHDRAW_TO_OTHERS") && ( */}
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">All Banks</label>
                          <RSelect
                            placeholder="Enter Bank"
                            options={allBanks}
                            onChange={(e) => setFormData({ ...formData, currentBank: e.label })}
                            // ref={register({ required: "This field is required" })}
                          />
                        </FormGroup>
                      </Col>
                      {/* )} */}
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Account Number</label>
                          <input
                            type="text"
                            name="bankAccountDetails"
                            placeholder="Enter Account Number"
                            onChange={onInputChange}
                            className="form-control"
                            ref={register({ required: "This field is required" })}
                          />
                          {accountName && formData?.bankAccountDetails && (
                            <span className="invalid" style={{ color: "green" }}>
                              {accountName}
                            </span>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label">Upload Customer's Instructions</label>
                          <input
                            type="file"
                            name="customerInstructionImage"
                            placeholder="Select Customer's Instructions"
                            onChange={(e) => handleInvestmentFileChange(e, "customerInstructionImage")}
                            className="form-control"
                            ref={register({ required: "This field is required" })}
                          />
                          {errors.customerInstructionImage && (
                            <span className="invalid">{errors.customerInstructionImage.message}</span>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="12">
                        <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                          <li>
                            {isLoadBtn ? (
                              <Button color="primary" disabled>
                                <Spinner size="sm">Loading...</Spinner>
                                <span> Loading</span>
                              </Button>
                            ) : (
                              <Button
                                color="primary"
                                size="md"
                                type="submit"
                                disabled={
                                  !formData.withdrawTo ||
                                  !formData.bankAccountDetails ||
                                  !formData.customerInstructionImage ||
                                  !formData.email ||
                                  !accountName ||
                                  !formData.currentBank // ✅ required for both TO_BANK and WITHDRAW_TO_OTHERS
                                }
                              >
                                Submit
                              </Button>
                            )}
                          </li>
                          <li>
                            <Button onClick={onFormCancel} className="link link-light">
                              Cancel
                            </Button>
                          </li>
                        </ul>
                      </Col>
                    </>
                  )}
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};
